import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import '@/App.css';
import '@/Checklist.css';
import '@/Auth.css';
import { api } from '@/api';
import { AuthContext } from '@/auth';
import PageFrame from '@/components/PageFrame';
import Checklist from '@/pages/Checklist';
import Login from '@/pages/Login';
import Settings from '@/pages/Settings';

// Owner (2026-10-11): sign-in, sign-out, reloads and the page frame work as on the B-BBEE site.

// Every page opens at the very top (no remembered scroll position)
if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

// Owner (2026-10-11, as on the B-BBEE site): the website's pages can't be printed - Ctrl/Cmd+P is blocked,
// and printing from the browser menu or right-click prints only a notice (@media print in Auth.css).
function PrintBlocker() {
  const [notice, setNotice] = useState(false);
  useEffect(() => {
    const blockShortcut = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'p' || e.key === 'P')) {
        e.preventDefault();
        e.stopPropagation();
        setNotice(true);
      }
    };
    const beforePrint = () => setNotice(true);
    window.addEventListener('keydown', blockShortcut, true);
    window.addEventListener('beforeprint', beforePrint);
    return () => {
      window.removeEventListener('keydown', blockShortcut, true);
      window.removeEventListener('beforeprint', beforePrint);
    };
  }, []);
  if (!notice) return null;
  return (
    <div className="modal-overlay" data-testid="print-blocked-dialog" onClick={() => setNotice(false)}>
      <div className="modal-content tca-problem-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Printing Disabled</h2>
        <p className="modal-description">Printing this website is disabled.</p>
        <div className="modal-actions">
          <button type="button" className="send-btn" onClick={() => setNotice(false)} data-testid="button-print-blocked-ok">OK</button>
        </div>
      </div>
    </div>
  );
}

// Signing in always opens the checklist (not the page the user was on when they signed out, e.g. Settings).
// A page reload while signed in stays where it is.
function SignIn({ onSignedIn }) {
  const navigate = useNavigate();
  return (
    <PageFrame subtitle="Please sign in to continue.">
      <Login
        onSignedIn={(u) => {
          navigate('/', { replace: true });
          onSignedIn(u);
        }}
      />
    </PageFrame>
  );
}

// Settings has its own address (/settings: a reload stays there, the browser's Back button works). The
// checklist stays mounted underneath while Settings shows, so its progress isn't lost; signing out reloads
// the page, so the next person starts with a fresh checklist.
function SignedIn({ user }) {
  const { pathname } = useLocation();
  const onSettings = pathname === '/settings' && user.role === 'admin';
  return (
    <PageFrame subtitle={onSettings ? 'Users, access and Xero.' : 'Work through the steps in order. Each step opens once the one before it is completed.'}>
      <Checklist hidden={onSettings} />
      <Routes>
        <Route path="/" element={null} />
        <Route path="/settings" element={user.role === 'admin' ? <Settings /> : <Navigate to="/" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </PageFrame>
  );
}

function App() {
  // undefined while checking the session, null when signed out
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    api('/auth/me')
      .then(setUser)
      .catch(() => {
        // Signed out / session ended (e.g. a page opened again after 8 hours): the sign-in page shows on the
        // front address. The router isn't running yet, so the address is just replaced.
        if (window.location.pathname !== '/') window.history.replaceState(null, '', '/');
        setUser(null);
      });
  }, []);

  // Signing out reloads the website's front address (not the page the user was on, e.g. /settings);
  // replace, so Back doesn't return to the signed-in page
  const signOut = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch {
      // signed out anyway as far as this page is concerned; the reload checks the session again
    }
    window.location.replace('/');
  }, []);

  // setUser: Settings → Edit User on your own account updates the name shown in the account bars
  const auth = useMemo(() => ({ user, setUser, signOut }), [user, signOut]);

  if (user === undefined) {
    return (
      <div className="tca-boot">
        <Loader2 size={30} className="tca-spin" />
      </div>
    );
  }

  return (
    <AuthContext.Provider value={auth}>
      <BrowserRouter>
        <PrintBlocker />
        <ScrollToTop />
        {/* Invitation only: every page needs a signed-in user (the sign-in page shows on any address) */}
        {user ? <SignedIn user={user} /> : <SignIn onSignedIn={setUser} />}
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
